import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Receipt, Search, Clock, ArrowRight, X } from 'lucide-react';
import PrintScheduleButton from '@/components/PrintScheduleButton';

// Filter Banner Component
const FilterBanner = ({ filterName, count, onClear, color = 'red' }) => {
  const colorClasses = {
    red: 'bg-red-100 border-red-300 text-red-800',
  };
  return (
    <div className={`mb-4 p-3 rounded-lg border ${colorClasses[color] || colorClasses.red} flex items-center justify-between`}>
      <span className="font-medium">
        Showing {count} {filterName} claim{count !== 1 ? 's' : ''}
      </span>
      <Button variant="ghost" size="sm" onClick={onClear} className="h-8">
        <X className="h-4 w-4 mr-1" /> Clear Filter
      </Button>
    </div>
  );
};

export default function ClaimsTab({
  allClaims,
  filteredClaims,
  claimSearch,
  setClaimSearch,
  claimStatusFilter,
  setClaimStatusFilter,
  urlFilter,
  getFilterName,
  clearFilter,
  project
}) {
  const navigate = useNavigate();

  return (
    <>
      {/* Filter Banner */}
      {urlFilter && getFilterName && getFilterName(urlFilter) && (
        <FilterBanner 
          filterName={getFilterName(urlFilter)} 
          count={filteredClaims.length} 
          onClear={clearFilter}
          color="red"
        />
      )}
      
      {/* Back Button */}
      <Button
        onClick={() => navigate('/')}
        variant="ghost"
        className="mb-4 text-slate-600 hover:text-slate-900"
        data-testid="claims-back-button"
      >
        ← Back to Dashboard
      </Button>
      
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-barlow font-semibold uppercase">EOT Claims</h2>
        <PrintScheduleButton 
          moduleType="Claim"
          data={allClaims}
          project={project}
        />
      </div>

      {/* Filter Controls for Claims */}
      <div className="flex flex-wrap gap-4 mb-6 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search claims..."
            value={claimSearch}
            onChange={(e) => setClaimSearch(e.target.value)}
            className="pl-9"
            data-testid="claim-search"
          />
        </div>
        <Select value={claimStatusFilter} onValueChange={setClaimStatusFilter}>
          <SelectTrigger className="w-40" data-testid="claim-status-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="under_review">Under Review</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
          </SelectContent>
        </Select>
        {(claimSearch || claimStatusFilter !== 'all') && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => { setClaimSearch(''); setClaimStatusFilter('all'); }}
            className="text-slate-500"
          >
            <X className="h-4 w-4 mr-1" /> Clear
          </Button>
        )}
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card className="rounded-sm">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-slate-900">{allClaims.length}</p>
            <p className="text-xs text-slate-500 uppercase font-medium">Total Claims</p>
          </CardContent>
        </Card>
        <Card className="rounded-sm">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-amber-600">{allClaims.filter(c => (c.status || '').toLowerCase() === 'draft').length}</p>
            <p className="text-xs text-slate-500 uppercase font-medium">Draft</p>
          </CardContent>
        </Card>
        <Card className="rounded-sm">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-blue-600">{allClaims.filter(c => ['submitted', 'under_review'].includes((c.status || '').toLowerCase())).length}</p>
            <p className="text-xs text-slate-500 uppercase font-medium">In Progress</p>
          </CardContent>
        </Card>
        <Card className="rounded-sm">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-green-600">{allClaims.filter(c => (c.status || '').toLowerCase() === 'approved').length}</p>
            <p className="text-xs text-slate-500 uppercase font-medium">Approved</p>
          </CardContent>
        </Card>
      </div>

      {filteredClaims.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed border-slate-300">
          <Receipt className="h-12 w-12 mx-auto text-slate-400 mb-4" />
          <p className="text-slate-600 font-medium">
            {urlFilter || claimSearch || claimStatusFilter !== 'all' ? 'No Matching Claims' : 'No claims created yet'}
          </p>
          <p className="text-sm text-slate-500 mt-1">
            {urlFilter || claimSearch || claimStatusFilter !== 'all' ? 'No claims match the current filter' : 'Convert a notice to a claim to see it here'}
          </p>
          {(urlFilter || claimSearch || claimStatusFilter !== 'all') && (
            <Button variant="outline" onClick={() => { clearFilter(); setClaimSearch(''); setClaimStatusFilter('all'); }} className="mt-4">
              Clear Filters
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredClaims.map((claim) => {
            const status = (claim.status || 'draft').toLowerCase();
            const statusDisplay = claim.status ? claim.status.charAt(0).toUpperCase() + claim.status.slice(1) : 'Draft';
            return (
              <div
                key={claim.id}
                onClick={() => navigate(`/claims/${claim.id}`)}
                className="bg-white border border-slate-200 rounded-sm p-4 cursor-pointer hover:border-slate-400 hover:shadow-sm transition-all group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold uppercase">
                        {claim.claim_number || `Claim ${claim.id?.substring(0, 8)}`}
                      </h3>
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        EOT CLAIM
                      </Badge>
                      <Badge variant="outline" className={
                        status === 'draft' ? 'bg-slate-100 text-slate-600' :
                        status === 'submitted' ? 'bg-blue-50 text-blue-600' :
                        status === 'under_review' ? 'bg-amber-50 text-amber-600' :
                        status === 'approved' ? 'bg-green-50 text-green-600' :
                        'bg-slate-100'
                      }>
                        {statusDisplay}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-2 mb-3">
                      {claim.impact_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'EOT Claim'}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span className="font-mono">
                          {claim.created_at 
                            ? new Date(claim.created_at).toLocaleDateString() 
                            : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-secondary group-hover:translate-x-1 transition-transform" strokeWidth={2} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
